import React, { useState, useEffect } from 'react';
import { pointsApi, productApi } from '../services';

function PointsPage({ onNavigate }) {
  const [userPoints, setUserPoints] = useState(0);
  const [pointHistory, setPointHistory] = useState([]);
  const [redeemProducts, setRedeemProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [redeemingProduct, setRedeemingProduct] = useState(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Check if user is logged in
    const userInfoData = localStorage.getItem('user_info');
    if (userInfoData) {
      try {
        const userData = JSON.parse(userInfoData);
        setUserInfo(userData);
        loadUserPoints();
        loadPointHistory();
        loadRedeemProducts();
      } catch (error) {
        console.error('Error parsing user info:', error);
        setError('Vui lòng đăng nhập để xem điểm thưởng');
        setLoading(false);
      }
    } else {
      setError('Vui lòng đăng nhập để xem điểm thưởng');
      setLoading(false);
    }
  }, []);

  const loadUserPoints = async () => {
    try {
      const response = await pointsApi.getCustomerInfo();
      console.log('Customer info response:', response);
      if (response.ok && response.payload) {
        setUserPoints(response.payload.customer_point || 0);
      } else {
        setUserPoints(0);
      }
    } catch (error) {
      console.error('Error loading customer info:', error);
      setError('Không thể tải thông tin điểm thưởng');
    }
  };

  const loadPointHistory = async () => {
    try {
      const response = await pointsApi.getCustomerPointHistory();
      console.log(response);
      if (response.ok && Array.isArray(response.payload)) {
        // Sort by create_datetime in descending order (newest first)
        const sortedHistory = response.payload.sort((a, b) => {
          const dateA = new Date(a.create_datetime || a.created_at || a.date);
          const dateB = new Date(b.create_datetime || b.created_at || b.date);
          return dateB - dateA;
        });
        setPointHistory(sortedHistory);
      } else {
        setPointHistory([]);
      }
    } catch (error) {
      console.error('Error loading point history:', error);
      setError('Không thể tải lịch sử điểm thưởng');
    } finally {
      setLoading(false);
    }
  };

  const loadRedeemProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await productApi.getRedeemProducts({
        page_size: 20,
        page_number: 1
      });
      console.log('Redeem products response:', response);
      if (response.ok && Array.isArray(response.payload)) {
        setRedeemProducts(response.payload);
      } else {
        setRedeemProducts([]);
      }
    } catch (error) {
      console.error('Error loading redeem products:', error);
      // Don't set main error for products, just log it
    } finally {
      setLoadingProducts(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getTransactionType = (type) => {
    return type == 1 ? 'earn' : 'redeem';
  };

  const getTransactionLabel = (points, description, type) => {
    if (description) return description;
    return type == 1 ? 'Bạn đã tích' : 'Bạn đã đổi';
  };

  const getProductName = (product_id) => {
    const product = redeemProducts.find(p => p.product_id == product_id);
    return product ? product.name : '';
  };

  const handleRedeemProduct = (product) => {
    const requiredPoints = product.redeem_point || product.point;
    if (userPoints >= requiredPoints) {
      setRedeemingProduct(product);
      setShowRedeemModal(true);
    }
  };

  const confirmRedeem = async () => {
    if (!redeemingProduct) return;

    setRedeemLoading(true);
    try {
      const redeemData = {
        list_products: [
          {
            product_id: redeemingProduct.product_id || redeemingProduct.id,
            quantity: 1
          }
        ],
        redeem_points: redeemingProduct.redeem_point || redeemingProduct.point
      };

      const response = await pointsApi.redeemProduct(redeemData);
      console.log('Redeem response:', response);

      if (response.ok) {
        // Success - reload user points and close modal
        await loadUserPoints();
        await loadPointHistory(); // Reload history to show new transaction
        setShowRedeemModal(false);
        setRedeemingProduct(null);
        
        // Show success popup instead of alert
        setSuccessMessage(`Đổi sản phẩm "${redeemingProduct.name}" thành công!`);
        setShowSuccessPopup(true);
        
        // Auto hide success popup after 3 seconds
        setTimeout(() => {
          setShowSuccessPopup(false);
          setSuccessMessage('');
        }, 3000);
      } else {
        alert(response.message || 'Đổi sản phẩm thất bại. Vui lòng thử lại.');
      }
    } catch (error) {
      console.error('Error redeeming product:', error);
      alert('Có lỗi xảy ra khi đổi sản phẩm. Vui lòng thử lại.');
    } finally {
      setRedeemLoading(false);
    }
  };

  const cancelRedeem = () => {
    setShowRedeemModal(false);
    setRedeemingProduct(null);
  };

  const handleBack = () => {
    if (onNavigate) onNavigate('/');
    else window.history.back();
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 40 }}>
      <div style={{ background: '#fff', width: 320, minHeight: 600, borderRadius: 8, padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
        <button onClick={handleBack} style={backBtnStyle}>&larr;</button>
        {/* User Info */}
        {userInfo && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#5b6754' }}>
              {userInfo.name}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>
              {userInfo.username}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 24 }}>
          <div style={{ width: 160, height: 160, borderRadius: '50%', background: '#5b6754', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 48, margin: '0 auto' }}>
            {loading ? '...' : userPoints}
            <div style={{ fontSize: 20, fontWeight: 400, marginTop: -8 }}>Điểm</div>
          </div>
        </div>

                 {/* Redeem Products Section */}
         <div style={{ width: '100%', marginTop: 32 }}>
           <div style={{ fontSize: 18, fontWeight: 600, color: '#5b6754', marginBottom: 16 }}>
             Sản phẩm đổi điểm
           </div>

           {loadingProducts && (
             <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
               Đang tải sản phẩm...
             </div>
           )}

           {!loadingProducts && redeemProducts.length === 0 && (
             <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
               Hiện tại chưa có sản phẩm đổi điểm
             </div>
           )}

           {!loadingProducts && redeemProducts.length > 0 && (
             <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
               {redeemProducts.map((product) => (
                  product.redeem_flg === 1 && <div
                   key={product.product_id || product.id}
                   style={{
                     background: '#f8f8f8',
                     borderRadius: 12,
                     padding: 12,
                     boxShadow: '0 2px 8px rgba(91,103,84,0.04)',
                     cursor: 'pointer',
                     transition: 'transform 0.2s ease',
                     border: '1px solid #d9d9d9',
                   }}
                   onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                   onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                 >
                   {product.image && (
                     <div style={{
                       width: '100%',
                       height: 80,
                       borderRadius: 8,
                       background: `url(${product.image}) center/cover`,
                       marginBottom: 8
                     }} />
                   )}
                   <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, lineHeight: '1.2' }}>
                     {product.name}
                   </div>
                   <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                     {product.description}
                   </div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                     <div style={{ fontSize: 14, fontWeight: 600, color: '#F9A23B' }}>
                       {product.redeem_point || product.point} điểm
                     </div>
                     <button
                       style={{
                         background: userPoints >= (product.redeem_point || product.point) ? '#5b6754' : '#ccc',
                         color: '#fff',
                         border: 'none',
                         borderRadius: 6,
                         padding: '4px 8px',
                         fontSize: 12,
                         cursor: userPoints >= (product.redeem_point || product.point) ? 'pointer' : 'not-allowed',
                         fontFamily: 'NeueEinstellung'
                       }}
                       disabled={userPoints < (product.redeem_point || product.point)}
                       onClick={() => handleRedeemProduct(product)}
                     >
                       Đổi
                     </button>
                   </div>
                 </div>
               ))}
             </div>
           )}
         </div>

        {/* Error Message */}
        {error && (
          <div style={{ 
            color: '#ff4444', 
            fontSize: 14, 
            marginTop: 16,
            textAlign: 'center',
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: 8,
            padding: '12px 16px',
            width: '100%'
          }}>
            {error}
          </div>
        )}

        {/* Point History */}
        <div style={{ width: '100%', marginTop: 32 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#5b6754', marginBottom: 16 }}>Lịch sử điểm thưởng</div>
          {loading && !error && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
              Đang tải lịch sử điểm thưởng...
            </div>
          )}

          {!loading && !error && pointHistory.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#888' }}>
              Bạn chưa có lịch sử điểm thưởng nào
            </div>
          )}

          {!loading && pointHistory.length > 0 && pointHistory.map((item, idx) => {
            const transactionType = getTransactionType(item.type);
            const transactionLabel = getTransactionLabel(item.point, item.description, item.type);
            
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', border: '1px solid #d9d9d9', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ width: 10, height: 32, borderRadius: 8, background: transactionType === 'redeem' ? '#F9A23B' : '#576550', marginRight: 12 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{transactionLabel}</div>
                  <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                    {formatDate(item.create_datetime || item.created_at || item.date)}
                  </div>
                  {item.order_id && (
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      Đơn hàng: #{item.order_id}
                    </div>
                  )}
                  {item.product_id && (
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      {getProductName(item.product_id)}<span style={{color: '#ff4444', }}>{item.is_used ? ' (Đã sử dụng)': ''}</span>
                    </div>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 20, color: transactionType === 'redeem' ? '#F9A23B' : '#576550' }}>
                  {item.type == 1 ? '+' : ''}{item.point}
                </div>
              </div>
            );
          })}
        </div>

        {!loading && pointHistory.length > 5 && (
          <div style={{ marginTop: 16, color: '#888', fontSize: 13, textAlign: 'center', width: '100%', cursor: 'pointer' }}
               onClick={() => onNavigate && onNavigate('/order-history')}>
            Xem thêm
          </div>
        )}

        {/* Redeem Confirmation Modal */}
        {showRedeemModal && redeemingProduct && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 280,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
            }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#5b6754', marginBottom: 8 }}>
                  Xác nhận đổi sản phẩm
                </div>
                <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                  {redeemingProduct.name}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#F9A23B' }}>
                  {redeemingProduct.redeem_point || redeemingProduct.point} điểm
                </div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  Số dư sau khi đổi: {userPoints - (redeemingProduct.redeem_point || redeemingProduct.point)} điểm
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={cancelRedeem}
                  disabled={redeemLoading}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: '#f0f0f0',
                    color: '#666',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    cursor: redeemLoading ? 'not-allowed' : 'pointer',
                    fontFamily: 'NeueEinstellung'
                  }}
                >
                  Hủy
                </button>
                <button
                  onClick={confirmRedeem}
                  disabled={redeemLoading}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: redeemLoading ? '#ccc' : '#5b6754',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    cursor: redeemLoading ? 'not-allowed' : 'pointer',
                    fontFamily: 'NeueEinstellung'
                  }}
                >
                  {redeemLoading ? 'Đang xử lý...' : 'Xác nhận đổi'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Popup */}
        {showSuccessPopup && successMessage && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 24,
              width: 280,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#5b6754', marginBottom: 8 }}>
                Thành công!
              </div>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                {successMessage}
              </div>
              <button
                onClick={() => setShowSuccessPopup(false)}
                style={{
                  background: '#5b6754',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 16px',
                  fontSize: 14,
                  cursor: 'pointer',
                  fontFamily: 'NeueEinstellung'
                }}
              >
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const backBtnStyle = {
  position: 'absolute',
  top: 20,
  left: 20,
  background: 'none',
  border: 'none',
  fontSize: 28,
  color: '#5b6754',
  cursor: 'pointer',
  zIndex: 2,
  padding: 0,
};

const btnStyle = {
  width: 160,
  height: 48,
  background: '#5b6754',
  color: '#fff',
  border: 'none',
  borderRadius: 24,
  fontSize: 18,
  margin: '12px 0',
  cursor: 'pointer',
  fontFamily: 'NeueEinstellung',
  fontWeight: 400,
};

export default PointsPage;
